<script>
import control from "@common/control/control.vue";
import labelWrapper from "@common/control/labelWrapper.vue";
import { library } from "@common/library/library";

const editorSettings = {};

const quicklinkComposerConfig = {
    cId: "quicklinkComposer",
    cType: "quicklinkComposer",
    cTitle: "Quicklink Composer",
    cTemplate: `<div id="dialog-template-generate-quicklink">
        <div style="margin-bottom: 10px;">
            <control name="SELECT_ALL" :value="includeAllControls" @update:value="includeAllControls = $event"></control>
        </div>
        <div v-if="!includeAllControls" style="border-top: 1px solid #ccc;">
            <label-wrapper :label-settings="labelConfig"></label-wrapper>
            <div v-for="controlItem in controlsList" class="quicklink-item-wrapper">
                <div class="control-item-label">{{controlItem.cId}}</div>
                <div v-if="controlItem.controlConfig">
                    <control :name="controlItem.controlConfig.name" :value="quicklinkSelections[controlItem.controlConfig.cId].controls" @update:value="quicklinkSelections[controlItem.controlConfig.cId].controls = $event" :prop-config="controlItem.controlConfig"></control>
                </div>
                <div v-if="controlItem.propConfig">
                    <control :name="controlItem.propConfig.name" :value="quicklinkSelections[controlItem.propConfig.cId]" @update:value="quicklinkSelections[controlItem.propConfig.cId] = $event" :prop-config="controlItem.propConfig"></control>
                </div>
            </div>
        </div>
        <div style="border-top: 1px solid #ccc;">
            <control name="DOMAIN_URL" :value="domainUrl" @update:value="domainUrl = $event"></control>
        </div>
        <div>
            <control name="JIRA_TICKET_URL" :value="jiraTicketUrl" @update:value="jiraTicketUrl = $event"></control>
        </div>
        <control name="GENERATE_BUTTON"></control><br/><br/>
        <div class="generated-link-container" v-if="generatedLink">
            <p class="generated-link-text">{{generatedLink}}</p>
            <div class="generated-link-actions">
                <button type="button" @click="copyUrl" class="quicklink-button">{{ isCopied ? "Copied!" : "Copy" }}</button>
                <button type="button" @click="copyAndReloadUrl" class="quicklink-button">Reload</button>
            </div>
        </div>
    </div>`,
    controls: [
        {
            name: 'SELECT_ALL',
            id: 'useDefaultGeneratedProps',
            label: 'Include All Controls',
            type: 'boolean',
            default: true,
            removeOnDestroy: true,
            helpText: 'If enabled, each control with a changed value will be added to the link. Otherwise you can choose which controls to add to your link.'
        },
        {
            name: 'GENERATE_BUTTON',
            id: 'generateButtonControl',
            label: 'Generate Link',
            type: 'button',
            onclick: 'generateLink',
            removeOnDestroy: true
        },
        {
            name: 'DOMAIN_URL',
            id: 'domainControl',
            items: [
                { label: 'https://devdash.samaritan.com/', value: 'https://devdash.samaritan.com/' },
                { label: 'https://devdashqa.samaritan.com/', value: 'https://devdashqa.samaritan.com/' },
                { label: 'localhost/', value: 'http://localhost/' }
            ],
            label: 'Domain',
            type: 'radio_button_list',
            removeOnDestroy: true
        },
        {
            name: 'JIRA_TICKET_URL',
            id: 'jiraTicketUrlControl',
            label: 'Jira URL',
            type: 'string',
            removeOnDestroy: true,
            helpText: '<span>e.g. https://samaritan.atlassian.net/browse/DEVD-1</span>'
        }
    ]
};

const quickLinkComposeCmp = {
    name: 'quicklinkComposer',
    editorSettings,
    props: {
        quicklinkObj: {
            type: Object
        },
    },
    data() {
        return {
            generatedLink: false,
            isCopied: false,
            quicklinkSelections: {},
            domainUrl: '',
            jiraTicketUrl: '',
            includeAllControls: true,
            controlsList: [],
            labelConfig: {
                noInputLabel: true,
                id: 'controlsLabel',
                label: 'Controls',
                helpText: 'Select the component properties and/or controls you want to include in your quicklink.'
            }
        };
    },
    methods: {
        createControls() {
            let output = [];
            let index = 0;
            // Service to break down controls property
            let addProps = (prop, value, items, isControl) => {
                let propTypeLabel = isControl ? 'Control' : 'Data';
                items.push({
                    label: `${propTypeLabel}: ${prop} (${value})`,
                    value: prop
                });
            };
            // Create each checkbox list using state properties and control values as checkbox values/labels
            for (let cId in this.quicklinkObj) {
                if (cId === 'length') continue;
                output[index] = {
                    cId,
                    controlConfig: false, // added below
                    propConfig: false // added below
                };
                let dataItems = [];
                let controlItems = [];
                this.quicklinkSelections[cId] = {}; // add cId to selections
                // Check for controls
                if (this.quicklinkObj[cId].controls) {
                    this.quicklinkSelections[cId].controls = {};
                    for (let prop in this.quicklinkObj[cId].controls) {
                        if (prop === 'length') continue;
                        this.quicklinkSelections[cId].controls[prop] = false;
                        let value = this.quicklinkObj[cId].controls[prop];
                        addProps(prop, value, controlItems, true);
                    }
                }
                // Check for data
                for (let prop in this.quicklinkObj[cId]) {
                    if (prop === 'length') continue;
                    if (prop !== 'controls') {
                        this.quicklinkSelections[cId][prop] = false;
                        let value = this.quicklinkObj[cId][prop];
                        addProps(prop, value, dataItems);
                    }
                }
                if (dataItems.length) {
                    output[index].propConfig = {
                        label: cId,
                        name: 'component_props_' + cId,
                        type: 'CHECKBOX_LIST',
                        id: 'controls_' + cId,
                        hideLabel: true,
                        cssClass: 'quicklink-checkbox-list',
                        cId,
                        items: dataItems,
                        removeOnDestroy: true
                    };
                }
                if (controlItems.length) {
                    output[index].controlConfig = {
                        label: cId,
                        name: 'component_controls_' + cId,
                        type: 'CHECKBOX_LIST',
                        id: 'controls_' + cId,
                        hideLabel: true,
                        cssClass: 'quicklink-checkbox-list',
                        cId,
                        items: controlItems,
                        removeOnDestroy: true
                    };
                    this.quicklinkSelections[cId].controls = {};
                }
                index++;
            }
            this.controlsList = output;
        },
        copyUrl() {
            library.copyTextToClipboard(this.generatedLink);
            this.isCopied = true;
            setTimeout(() => {
                this.isCopied = false;
            }, 3000);
        },
        copyAndReloadUrl() {
            library.copyTextToClipboard(this.generatedLink);
            window.location.href = this.generatedLink;
        },
        async generateLink() {
            let includedControls = !this.includeAllControls && this.quicklinkSelections;
            if (!await this.isJiraTicketUrlValid()) return;
            this.generatedLink = await library.getQuickLink(false, false, includedControls, this.domainUrl, this.jiraTicketUrl);
        },
        async isJiraTicketUrlValid() {
            let isValid = true;
            if (this.jiraTicketUrl) {
                if (this.jiraTicketUrl.indexOf('https://samaritan.atlassian.net/browse/') === -1) {
                    library.showDialog('Incorrect Jira URL', false, false, { title: 'Warning', showAsAlert: true });
                    isValid = false;
                }
            }
            return isValid;
        }
    },
    mounted() {
        let mountInterval = setInterval(() => {
            if (this.quicklinkObj) {
                clearInterval(mountInterval);
                this.createControls();
            }
        }, 100);
    },
    components: {
        control,
        labelWrapper
    },
};
componentStore.initComponent(quickLinkComposeCmp, quicklinkComposerConfig);
export { quicklinkComposerConfig, quickLinkComposeCmp };
export default quickLinkComposeCmp;
</script>

<style scoped>
.control-item-label {
    font-size: 18px;
    color: #666;
}
.quicklink-checkbox-list {
    margin: 0;
}
.quicklink-checkbox-list :deep(.checkboxes-container) {
    margin: 0;
}
.quicklink-item-wrapper {
    margin: 15px 0;
}
.generated-link-container {
    border-radius: 4px;
    background: #eee;
    border: 1px solid #ddd;
    display: flex;
    align-items: center;
    padding: 8px;
    gap: 10px;
}
.generated-link-text {
    flex-grow: 1;
    margin: 0;
    word-break: break-all;
}
.generated-link-actions {
    display: flex;
    gap: 5px;
}
.quicklink-button {
    border: 1px solid #ccc;
    padding: 6px 12px;
    color: #333;
    font-weight: normal;
    background: #fff;
    text-decoration: none;
    cursor: pointer;
    border-radius: 3px;
    transition: background-color 0.2s ease;
}
.quicklink-button:hover {
    background-color: #f0f0f0;
}
</style>
